import { useMemo } from "react";
import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { generateUUID } from "@/lib/utils";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";
import { toast } from "sonner";

export type GlobalDocument = {
  id: string;
  title: string;
  parent_id: string | null;
  is_folder: boolean;
  hidden_from_docs: boolean;
  bucket: GlobalDocumentBucket;
  document_id: string | null;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};
/** "managers" docs are visible to admins only. */
export type GlobalDocumentBucket = "team" | "managers";
export type GlobalDocumentLink = {
  id: string;
  global_document_id: string;
  link_type: string;
  link_id: string;
  created_at: string;
};
export type GlobalDocumentLinkType =
  | "project"
  | "project_group"
  | "global_document"
  | "project_note"
  | "project_debriefing"
  /** The shared SIM Cards docs (link id SIM_DOCS_LINK_ID). */
  | "sim_cards";

type ZeroGlobalDocument = {
  id: string;
  title: string;
  parentID?: string | null;
  isFolder: boolean;
  hiddenFromDocs?: boolean;
  bucket?: GlobalDocumentBucket;
  documentID?: string | null;
  sortOrder: number;
  createdBy?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroGlobalDocumentLink = {
  id: string;
  globalDocumentID: string;
  linkType: string;
  linkID: string;
  createdAt?: number | string | null;
};

function toLegacyDocument(row: ZeroGlobalDocument): GlobalDocument {
  return {
    id: row.id,
    title: row.title,
    parent_id: row.parentID ?? null,
    is_folder: row.isFolder,
    hidden_from_docs: row.hiddenFromDocs ?? false,
    bucket: row.bucket ?? "team",
    document_id: row.documentID ?? null,
    sort_order: row.sortOrder,
    created_by: row.createdBy ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyLink(row: ZeroGlobalDocumentLink): GlobalDocumentLink {
  return {
    id: row.id,
    global_document_id: row.globalDocumentID,
    link_type: row.linkType,
    link_id: row.linkID,
    created_at: toLegacyTimestamp(row.createdAt),
  };
}

export function useGlobalDocuments(includeHidden = false) {
  const [data, details] = useQuery(
    includeHidden
      ? appQueries.globalDocuments.all()
      : appQueries.globalDocuments.list(),
  );
  const docs = useMemo(
    () => ((data ?? []) as ZeroGlobalDocument[]).map(toLegacyDocument),
    [data],
  );
  return {
    data: docs,
    isLoading: details.type !== "complete" && docs.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateGlobalDocument() {
  const zero = useZero() as ZeroMutateClient;
  const { isOffline } = useOnlineStatus();
  const { isPending, runPending } = usePendingMutation();
  const run = (input: {
    title: string;
    parent_id?: string | null;
    is_folder?: boolean;
    hidden_from_docs?: boolean;
    bucket?: GlobalDocumentBucket;
  }) =>
    runPending(async () => {
      if (isOffline) {
        throw new Error("Documents can be created when you are online.");
      }

      const mutationInput = {
        id: generateUUID(),
        documentID: input.is_folder ? null : generateUUID(),
        title: input.title,
        parentID: input.parent_id ?? null,
        isFolder: input.is_folder ?? false,
        hiddenFromDocs: input.hidden_from_docs ?? false,
        bucket: input.bucket ?? "team",
      };
      const mutation = zero.mutate(
        appMutators.globalDocuments.create(mutationInput),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Global document create was rejected",
      );
      return toLegacyDocument({ ...mutationInput, sortOrder: 0 });
    });
  return {
    mutate: (
      input: {
        title: string;
        parent_id?: string | null;
        is_folder?: boolean;
        hidden_from_docs?: boolean;
        bucket?: GlobalDocumentBucket;
      },
      options?: MutationOptions<GlobalDocument>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Error creating document", {
            description: error.message,
          }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateGlobalDocument() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    id,
    ...updates
  }: {
    id: string;
    title?: string;
    parent_id?: string | null;
    sort_order?: number;
    bucket?: GlobalDocumentBucket;
  }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.globalDocuments.update({
          id,
          ...(updates.title !== undefined ? { title: updates.title } : {}),
          ...(updates.parent_id !== undefined
            ? { parentID: updates.parent_id }
            : {}),
          ...(updates.sort_order !== undefined
            ? { sortOrder: updates.sort_order }
            : {}),
          ...(updates.bucket !== undefined ? { bucket: updates.bucket } : {}),
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Global document update was rejected",
      );
      return { id };
    });
  return {
    mutate: (
      input: {
        id: string;
        title?: string;
        parent_id?: string | null;
        sort_order?: number;
        bucket?: GlobalDocumentBucket;
      },
      options?: MutationOptions<{ id: string }>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Error updating document", {
            description: error.message,
          }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteGlobalDocument() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (id: string) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.globalDocuments.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Global document delete was rejected",
      );
      return { id };
    });
  return {
    mutate: (id: string, options?: MutationOptions<{ id: string }>) => {
      void run(id)
        .then((data) => {
          toast.success("Document deleted");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Error deleting document", {
            description: error.message,
          }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useGlobalDocumentLinks(documentId: string | undefined) {
  const query = documentId
    ? appQueries.globalDocumentLinks.byDocument({ documentID: documentId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!documentId });
  const links = ((data ?? []) as ZeroGlobalDocumentLink[]).map(toLegacyLink);
  return {
    data: links,
    isLoading:
      !!documentId && details.type !== "complete" && links.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useGlobalDocumentLinksByEntity(
  linkType: GlobalDocumentLinkType,
  linkId: string | undefined,
) {
  const query = linkId
    ? appQueries.globalDocumentLinks.byEntity({
        linkType,
        linkID: linkId,
      })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!linkId });
  const links = ((data ?? []) as ZeroGlobalDocumentLink[]).map(toLegacyLink);
  return {
    data: links,
    isLoading: !!linkId && details.type !== "complete" && links.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateGlobalDocumentLink() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (input: {
    global_document_id: string;
    link_type: GlobalDocumentLinkType;
    link_id: string;
  }) =>
    runPending(async () => {
      const mutationInput = {
        id: generateUUID(),
        globalDocumentID: input.global_document_id,
        linkType: input.link_type,
        linkID: input.link_id,
      };
      const mutation = zero.mutate(
        appMutators.globalDocumentLinks.create(mutationInput),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Global document link create was rejected",
      );
      return toLegacyLink(mutationInput);
    });
  return {
    mutate: (
      input: {
        global_document_id: string;
        link_type: GlobalDocumentLinkType;
        link_id: string;
      },
      options?: MutationOptions<GlobalDocumentLink>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Error adding link", { description: error.message }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteGlobalDocumentLink() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    id,
    global_document_id,
  }: {
    id: string;
    global_document_id: string;
  }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.globalDocumentLinks.delete({
          id,
          globalDocumentID: global_document_id,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Global document link delete was rejected",
      );
      return { id, global_document_id };
    });
  return {
    mutate: (
      input: { id: string; global_document_id: string },
      options?: MutationOptions<{ id: string; global_document_id: string }>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Error removing link", { description: error.message }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
